import { useCallback, useMemo, useState } from 'react';

import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.constants';
import { AIOnboardingModalFormShape } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { FetchAiOnboardingModalDataV2Document } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModalV2.generated';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { useAssetCategoryOptions } from '@/modules/entities/StructuredAssetsSubform/hooks/useAssetCategoryOptions';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { PathsOf } from '@/types/subform';

interface AssetFieldsRowProps {
  index: number;
}

export function AssetFieldsRow({ index }: AssetFieldsRowProps) {
  const { entities } = useHouseholdDetailsContext();
  const {
    household: { id: householdId },
  } = useAIOnboardingModalContext();

  const { control, setValue } = useFormContext<AIOnboardingModalFormShape>();

  const { styles, columns } =
    AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS.assets;

  const ownerSelectOptions = useMemo(
    () =>
      (entities || []).map((entity) => ({
        value: entity.id,
        display: entity.subtype.displayName,
      })),
    [entities]
  );

  const { options: assetCategoryOptions } = useAssetCategoryOptions();

  const [isEntityModalOpen, setEntityModalOpen] = useState(false);

  const handleAfterCreateEntity = useCallback(
    (newEntityId: string) => {
      setValue(
        `assets.${index}.ownerId` as const satisfies PathsOf<AIOnboardingModalFormShape>,
        newEntityId
      );
      setEntityModalOpen(false);
    },
    [index, setValue]
  );

  return (
    <>
      {isEntityModalOpen && (
        <CreateEntityShortFormModal
          isOpen={isEntityModalOpen}
          onClose={() => setEntityModalOpen(false)}
          householdId={householdId}
          isOnboardingFlow={false}
          onAfterCreate={handleAfterCreateEntity}
          ignoredQueryDocuments={[FetchAiOnboardingModalDataV2Document]}
        />
      )}
      <FormLayoutRow sx={{ gap: styles.columnPaddingHorizontal }}>
        <FormLayoutItem width={columns.assetName.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `assets.${index}.assetName` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.assetName.columnHeader}
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.ownerId.columnWidth}>
          <FormAwareTypeaheadSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `assets.${index}.ownerId` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.ownerId.columnHeader}
            options={ownerSelectOptions}
            hideLabel
            required
            showErrorBeforeSubmit
            addNewOption={{
              onClick: () => setEntityModalOpen(true),
              text: 'Add entity',
            }}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow
        sx={{
          marginTop: -2,
          gap: styles.columnPaddingHorizontal,
          backgroundColor: COLORS.GRAY['100'],
          padding: 1,
          borderRadius: 0.75,
        }}
      >
        <FormLayoutItem width={columns.assetCategoryId.columnWidth}>
          <FormAwareSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `assets.${index}.assetCategoryId` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.assetCategoryId.columnSubheader ?? ''}
            options={assetCategoryOptions}
            showEmptyValue={false}
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.valuation.columnWidth}>
          <FormAwareCurrencyInput<AIOnboardingModalFormShape>
            isDecimalJSInput
            control={control}
            fieldName={
              `assets.${index}.valuation` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.valuation.columnSubheader ?? ''}
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </>
  );
}
